import { useEffect, useState } from "react";
import {
  registrationApprovalApi,
  type PendingRegistration,
  type RegistrationDetails,
} from "@/lib/api/registration-approval";
import { patronTypesApi, type PatronType } from "@/lib/api/patron-types";

function apiMessage(err: unknown, fallback: string): string {
  if (err && typeof err === "object" && "response" in err) {
    const message = (err as { response?: { data?: { message?: string } } }).response?.data?.message;
    if (message) return message;
  }
  return fallback;
}

export function useRejectedRegistrations() {
  const [registrations, setRegistrations] = useState<PendingRegistration[]>([]);
  const [patronTypes, setPatronTypes] = useState<PatronType[]>([]);
  const [loading, setLoading] = useState(true);
  const [busyAction, setBusyAction] = useState<"reopen" | "approve" | null>(null);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [details, setDetails] = useState<RegistrationDetails | null>(null);
  const [open, setOpen] = useState(false);

  const load = async () => {
    try {
      setLoading(true);
      const [rows, types] = await Promise.all([
        registrationApprovalApi.getRejectedRegistrations(),
        patronTypesApi.getAll(),
      ]);
      setRegistrations(rows);
      setPatronTypes(types);
      setError("");
    } catch (err: unknown) {
      setError(apiMessage(err, "Failed to load rejected registrations"));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openReview = async (userId: string) => {
    try {
      setDetails(await registrationApprovalApi.getRejectedRegistrationDetails(userId));
      setOpen(true);
      setError("");
    } catch (err: unknown) {
      setError(apiMessage(err, "Failed to load this application"));
    }
  };

  const run = async (
    action: "reopen" | "approve",
    task: () => Promise<string>,
    fallback: string
  ) => {
    if (!details) return;
    setBusyAction(action);
    try {
      setSuccess(await task());
      setOpen(false);
      setDetails(null);
      await load();
    } catch {
      setError(fallback);
    } finally {
      setBusyAction(null);
    }
  };

  return {
    registrations,
    patronTypes,
    loading,
    busyAction,
    error,
    success,
    details,
    open,
    setOpen,
    openReview,
    reopen: () =>
      run(
        "reopen",
        () => registrationApprovalApi.reopen(details!.id).then((r) => r.message),
        "Could not reopen this application"
      ),
    approve: (input: { patronTypeId?: string; homeBranchId?: string; notes?: string }) =>
      run(
        "approve",
        () =>
          registrationApprovalApi
            .approveOrReject(details!.id, { action: "approve", ...input })
            .then((r) => r.message),
        "Could not approve this application"
      ),
  };
}
